import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { BlogGateFailureView } from "../../../shared/blog-gate-failure";

export type BriefDraftResult = {
  status: "started" | "running" | "already_drafted";
  run_id: string | null;
  brief_id: string;
  state: string;
  task_slug: string | null;
};

/** PRD-0189 C: "Draft this" — the server decides what is drafted from the brief itself. */
export function useDraftBrief(brandId?: string, campaignId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (briefId: string) => {
      if (!campaignId) throw new Error("Choose the campaign before drafting.");
      const response = await api.post<{ data: BriefDraftResult }>(
        `/api/campaigns/${encodeURIComponent(campaignId)}/briefs/${encodeURIComponent(briefId)}/draft`, {});
      return response.data;
    },
    onSuccess: (_data, briefId) => {
      void qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      void qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      void qc.invalidateQueries({ queryKey: ["brief-draft-attempt", campaignId, briefId] });
    },
    onError: (_error, briefId) => {
      // A refusal (e.g. SURFACE_NOT_ACTIVE) may mean the readiness shown before the click is stale.
      void qc.invalidateQueries({ queryKey: ["brief-draft-readiness", campaignId, briefId] });
    },
  });
}

export type BriefDraftReadiness = {
  brief_id: string;
  surface: string | null;
  surface_required: boolean;
  /** Null when the brief's surface needs no brand surface (blog). */
  surface_active: boolean | null;
  task_slug: string | null;
};

/**
 * Before the click: can Draft this launch for this brief's surface? The server answers with the
 * runner's own active-surface check. Blog briefs need no surface and are not asked.
 */
export function useBriefDraftReadiness(campaignId: string | undefined, briefId: string | undefined, surface: string | null) {
  const needsSurface = !!surface && surface.trim().toLowerCase() !== "blog";
  return useQuery({
    queryKey: ["brief-draft-readiness", campaignId, briefId],
    queryFn: async () => (await api.get<{ data: BriefDraftReadiness }>(
      `/api/campaigns/${encodeURIComponent(campaignId!)}/briefs/${encodeURIComponent(briefId!)}/draft-readiness`)).data,
    enabled: !!campaignId && !!briefId && needsSurface,
    retry: false,
    // Surfaces are added on another page; check again when the operator comes back.
    refetchOnWindowFocus: true,
  });
}

export type BriefDraftAttempt = {
  run_id: string;
  task_slug: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  summary: string | null;
  error: string | null;
  gate_failure: BlogGateFailureView | null;
};

/** Walkthrough item 4: the latest Draft this attempt for a brief (survives reloads); polls while it runs. */
export function useBriefDraftAttempt(campaignId?: string, briefId?: string) {
  return useQuery({
    queryKey: ["brief-draft-attempt", campaignId, briefId],
    queryFn: async () => (await api.get<{ data: BriefDraftAttempt | null }>(
      `/api/campaigns/${encodeURIComponent(campaignId!)}/briefs/${encodeURIComponent(briefId!)}/draft-attempt`)).data,
    enabled: !!campaignId && !!briefId,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "running" || status === "retrying" || status === "queued" ? 5000 : false;
    },
  });
}

/** Walkthrough item 3: rebuild a pre-draft brief's canonical brief from its Opportunities idea. */
export function useRegenerateCanonicalBrief(brandId?: string, campaignId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (briefId: string) => {
      if (!campaignId) throw new Error("Choose the campaign before regenerating.");
      const response = await api.post<{ data: { brief_id: string; canonical_brief_origin: "llm" | "template" | null } }>(
        `/api/campaigns/${encodeURIComponent(campaignId)}/briefs/${encodeURIComponent(briefId)}/canonical-brief/regenerate`, {});
      return response.data;
    },
    onSuccess: (_data, briefId) => {
      void qc.invalidateQueries({ queryKey: ["brief-document", campaignId, briefId] });
      void qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
    },
  });
}
